import React from 'react';
import WorkspaceItemContext from '../../app/contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import {
    deleteDrawingObjectWorkspace,
    editDrawingObjectWorkspace,
    enableHoldingAnalyzer,
    propertiesDrawingObjectWorkspace
} from '../../app/state/slices/workspace-slice';
import { EditOutline, FileExportOutline, InfoCircleOutline, StopCircleOutline, TrashOutline } from '../../icons/icons';
import { DrawingObjectContextMenuData } from '../../services/map-interaction-service';
import { Menu, MenuItem } from '../common/menu/DropdownMenu';
import { exportDrawingObject } from './export';
import { DrawingObject } from './types';

export function DrawingObjectContextMenu(props: { info: DrawingObjectContextMenuData }) {
    const workspaceContextId = React.useContext(WorkspaceItemContext).id;
    const context = props.info;
    const objects = useAppSelector((x) => x.drawingObjects.objects);
    const drawingObjects = React.useMemo(
        () => context.objectIds.map((id) => objects.find((x) => x.id === id)).filter((x) => !!x),
        [context.objectIds, objects]
    );
    const canEditCompanyViews = useAppSelector((x) => x.user.rights.canEditCompanyViews);
    const dispatch = useAppDispatch();

    const handleDelete = React.useCallback(
        (object: DrawingObject) => {
            if (!drawingObjects) {
                return;
            }
            dispatch(deleteDrawingObjectWorkspace({ object }));
        },
        [context]
    );

    const handleOpenHoldingAnalyzer = React.useCallback(
        (object: DrawingObject) => {
            dispatch(enableHoldingAnalyzer({ workspaceId: workspaceContextId, drawingObjectId: object.id }));
        },
        [drawingObjects]
    );

    const handleExport = React.useCallback(
        async (object: DrawingObject) => {
            if (!drawingObjects) {
                return;
            }
            await exportDrawingObject(object);
        },
        [drawingObjects]
    );

    const handleProperties = (object: DrawingObject) => {
        if (!drawingObjects) {
            return;
        }
        dispatch(propertiesDrawingObjectWorkspace({ object }));
    };

    const handleEdit = (object: DrawingObject) => {
        if (!drawingObjects) {
            return;
        }
        dispatch(editDrawingObjectWorkspace({ object }));
    };

    return drawingObjects.map((object) => {
        const canEditObject = !object?.isCompany || canEditCompanyViews;
        return (
            <Menu key={object.id} label={object.name}>
                <MenuItem icon={StopCircleOutline} label="Holding analyzer" onClick={() => handleOpenHoldingAnalyzer(object)} />
                <MenuItem
                    icon={TrashOutline}
                    label="Delete"
                    onClick={() => canEditObject && handleDelete(object)}
                    disabled={!canEditObject}
                />
                <MenuItem icon={FileExportOutline} label="Export" onClick={() => handleExport(object)} />
                <MenuItem
                    icon={EditOutline}
                    label="Edit graphically"
                    onClick={() => canEditObject && handleEdit(object)}
                    disabled={!canEditObject}
                />
                <MenuItem
                    icon={InfoCircleOutline}
                    label="Properties"
                    onClick={() => canEditObject && handleProperties(object)}
                    disabled={!canEditObject}
                />
            </Menu>
        );
    });
}
